import { R_RIM, TURRET_TOP_Y } from "./dimensions";
import { Mat4, Vec3, lookAt, multiply, perspective } from "./mat4";

// What both renderers are handed, and the camera they share. Keeping the
// framing in one place is what makes the WebGPU wheel and the 2D fallback
// read as the same object seen the same way, rather than two drawings of it.

export interface BallState {
  angle: number;
  radius: number;
  /** Centre height. */
  y: number;
}

export interface SceneState {
  wheelAngle: number;
  ball: BallState;
  /** Hidden before the first spin, when there is nothing to follow yet. */
  ballVisible: boolean;
  /** The winning field, or -1 while there isn't one. */
  highlight: number;
  /** 0…1, how hard the winner is glowing right now. */
  glow: number;
  /** Extra size on the winning chip. */
  highlightScale: number;
  /** Seconds since the wheel was mounted, for anything that breathes. */
  time: number;
}

export interface WheelRenderer {
  readonly kind: "webgpu" | "canvas";
  resize(cssWidth: number, cssHeight: number, dpr: number): void;
  render(state: SceneState): void;
  destroy(): void;
}

/**
 * How far the camera is tipped above the wheel's plane. Shallow enough that
 * the wheel is obviously a solid object standing on a table, steep enough that
 * every one of the 22 pockets stays open to the viewer instead of the far half
 * closing up into a line — which is the whole difficulty with drawing a
 * roulette wheel in 3D on a phone.
 */
export const CAMERA_ELEVATION = (55 * Math.PI) / 180;

/**
 * Where the key light hangs: high, in front, and off to the left. Shared,
 * because the shadow the wheel throws on the cloth has to be worked out from
 * it in both renderers, and a shadow lying the wrong way is worse than none.
 */
export const KEY_LIGHT: Vec3 = [-0.5, 0.92, 0.46];
export const CAMERA_FOV = (30 * Math.PI) / 180;
/** What the framing has to contain, with a little air round it. */
const FIT_RADIUS = R_RIM + 0.02;
/** Pulls the camera in past a bare fit, so the wheel fills more of the box. */
const ZOOM = 0.88;

export interface Camera {
  eye: Vec3;
  target: Vec3;
  viewProj: Mat4;
  /** Pixels per world unit at the wheel's plane — what the 2D painter draws by. */
  scale(cssHeight: number): number;
}

export function cameraFor(aspect: number): Camera {
  const halfFov = CAMERA_FOV / 2;
  const fitHeight = FIT_RADIUS * Math.sin(CAMERA_ELEVATION) + TURRET_TOP_Y * 0.6;
  // Fit whichever way round the box is: a tall, narrow phone column is limited
  // by its width, a short wide one by its height.
  const distance =
    Math.max(FIT_RADIUS / (Math.tan(halfFov) * aspect), fitHeight / Math.tan(halfFov)) * 1.01 * ZOOM;

  const target: Vec3 = [0, 0.05, 0];
  const eye: Vec3 = [
    target[0],
    target[1] + distance * Math.sin(CAMERA_ELEVATION),
    target[2] + distance * Math.cos(CAMERA_ELEVATION),
  ];
  const view = lookAt(eye, target, [0, 1, 0]);
  const viewProj = multiply(perspective(CAMERA_FOV, aspect, 0.1, 40), view);

  return {
    eye,
    target,
    viewProj,
    scale: (cssHeight: number) => cssHeight / 2 / (Math.tan(halfFov) * distance),
  };
}
